import usePrivateAxios from '@/common/api/usePrivateAxios';
import { ReasonValidationSchema } from '@/common/schemas/reason.schema';
import { transformNewDriverLicenseCategories } from '@/common/utils/driverLicenseCategoryTransformers';
import { getRailsUpdate } from '@/common/utils/getRailsUpdate';
import { mapToDeletion } from '@/common/utils/railsHelpers';
import { transformNewRoles } from '@/common/utils/roleTransformers';
import { removeSpecialCharacters } from '@/common/utils/unFormatters';
import { ClientUserValidationSchema } from '@/features/users/schemas/create.schema';
import { ClientUserData } from '@/features/users/types/clientUsers';
import { useMutation } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

interface UseUpdateClientUserProps {
  clientUserId?: string;
  clientUserData?: ClientUserData;
  file: Blob | undefined
  onSuccess: (response: AxiosResponse) => void;
  onError: (error: AxiosError) => void;
}

export const useUpdateClientUser = ({
  clientUserId,
  clientUserData,
  file,
  onSuccess,
  onError,
}: UseUpdateClientUserProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['updateClientUser'],
    mutationFn: async ({
      form,
      reasonForm,
    }: {
      form: ClientUserValidationSchema;
      reasonForm?: ReasonValidationSchema;
    }) => {
      const payload = {
        cpf: removeSpecialCharacters(form.cpf),
        date_of_birth: form.date_of_birth || null,
        user_roles_attributes: getRailsUpdate(
          clientUserData?.user_roles || [],
          form?.user_roles || [],
          (userRole) => userRole.role.id,
          (role) => role.value || '',
          mapToDeletion,
          transformNewRoles,
        ),
        status: form?.status?.value || 'enabled',
        full_name: form.full_name || '',
        branch_id: form?.branch_id?.value || null,
        phone_attributes: { number: form?.phone?.number ? removeSpecialCharacters(form?.phone?.number) : '' },
        email: form.email || null,
        enrolment_number: form.enrollment_number || null,
        profile_picture: file ? file : undefined,
        current_driver_license_registration_number: form.current_driver_license_registration_number || null,
        current_driver_license_expiration_date: form.current_driver_license_expiration_date || null,
        user_driver_license_categories_attributes: getRailsUpdate(
          clientUserData?.user_driver_license_categories || [],
          form?.user_driver_license_categories || [],
          (category) => category.license_category.id,
          (category) => category.value || '',
          mapToDeletion,
          transformNewDriverLicenseCategories,
        ),
      };

      delete reasonForm?.file_attachment_attributes?.file;

      return privateAxios.put(`/users/${clientUserId}`, {
        client_user: payload,
        log_reason: reasonForm,
      });
    },
    onSuccess,
    onError,
  });
};
